import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import Icon from '../Icon';
import SearchableSelect from '../ui/SearchableSelect';
import { useConfirm } from '../ui/ConfirmProvider';
import GuestRelationsPanel from './GuestRelationsPanel';
import { useNotifications } from '../NotificationProvider';
import { useRole } from '../../hooks/useRole';
import {
  archiveGuest, restoreGuest, deleteGuestPermanentOrThrow, fetchGuestInviteCode, getGuestById, loadGuests, loadPlusOnesOf, loadGuestTags,
} from '../../services/GuestService';
import {
  loadGuestInvitations, setGuestEventRsvp, removeGuestFromEvent, type GuestEventInvitation,
} from '../../services/GuestEventsService';
import { loadEvents, refreshEventsFromApi, type StoredEvent } from '../../services/EventsService';
import { effectiveRsvpStatus, hasRsvpMismatch } from '../../utils/guestRsvpStatus';
import { guestRoleLabel } from '../../utils/guestRoles';
import { sideLabel, normalizeSide } from '../../utils/guestSide';
import { isGuestArchived, canDeleteGuestPermanently } from '../../utils/guestArchive';
import { permanentDeleteErrorMessage, readableErrorMessage } from '../../utils/permanentDeleteError';
import { GUESTS_PATH, guestEditPath, guestPath } from '../../utils/guestRoutes';
import type { Guest, RSVPStatus } from '../../types/Guests';
import MaskedValue from '../ui/MaskedValue';

const RSVP_LABEL: Record<string, string> = {
  confirmed: 'Confirmed', declined: 'Declined', pending: 'Pending', awaiting_plus_one: 'Awaiting plus-one',
};
const RSVP_CHOICES = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
];

interface Props {
  guestRef: string;
}

/** One guest: contact, side, tags, answer per event, relations, plus-ones, and archive / restore / delete. */
const GuestProfile: React.FC<Props> = ({ guestRef }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotifications();
  const { isPlanner, isContractor } = useRole();
  // contractors sit at planner level but must not see a guest's login credential
  const canSeeInviteCode = isPlanner && !isContractor;
  const [guest, setGuest] = useState<Guest | null>(() => getGuestById(guestRef));
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<GuestEventInvitation[]>([]);
  const [events, setEvents] = useState<StoredEvent[]>(() => loadEvents());
  const [busy, setBusy] = useState(false);
  const [eventToAdd, setEventToAdd] = useState('');

  useEffect(() => { setGuest(getGuestById(guestRef)); }, [guestRef]);
  useEffect(() => {
    let cancelled = false;
    setInviteCode(null);
    // the invite code is a credential: asked for on this page only, and the API sends none below planner
    if (canSeeInviteCode) fetchGuestInviteCode(guestRef).then(code => { if (!cancelled) setInviteCode(code); });
    return () => { cancelled = true; };
  }, [guestRef, canSeeInviteCode]);
  const reloadInvitations = useCallback(() => {
    loadGuestInvitations(guestRef).then(setInvitations).catch(() => setInvitations([]));
  }, [guestRef]);
  useEffect(() => {
    reloadInvitations();
    refreshEventsFromApi().then(setEvents).catch(() => { /* events screen not available: the list below simply stays short */ });
  }, [reloadInvitations]);

  // each tag shows the colour the project's tag list gives it (the copy stored on the guest can be stale)
  const tagColours = useMemo(() => new Map(loadGuestTags().map(t => [t.id, t.color])), [guest]);
  const plusOnes = useMemo(() => loadPlusOnesOf(guestRef), [guestRef, guest]);
  const partyOf = guest?.party_of ? getGuestById(guest.party_of) ?? loadGuests().find(g => g.id === guest.party_of) ?? null : null;
  const inviteIds = useMemo(() => new Set(invitations.map(i => i.event_id)), [invitations]);
  const eventOptions = useMemo(
    () => events.filter(e => !inviteIds.has(String(e.id))).map(e => ({ value: String(e.id), label: e.title })),
    [events, inviteIds],
  );

  if (!guest) {
    return (
      <div className="gst-empty" role="status">
        <p>This guest could not be found. They may have been removed.</p>
        <Button variant="outline" onClick={() => navigate(GUESTS_PATH)}>Back to guests</Button>
      </div>
    );
  }

  const archived = isGuestArchived(guest);
  const status = effectiveRsvpStatus(guest);
  const side = normalizeSide(guest.side);

  const run = async (action: () => Promise<void>, done: string, failed: string) => {
    setBusy(true);
    try { await action(); showSuccess(done); setGuest(getGuestById(guestRef)); }
    catch (err) { showError(readableErrorMessage(err, failed)); }
    finally { setBusy(false); }
  };

  const onArchive = async () => {
    if (!(await confirm({ title: 'Archive this guest?', subject: guest.name, description: 'They leave the guest list but can be restored.', confirmLabel: 'Archive' }))) return;
    await run(() => archiveGuest(guest.id), 'Guest archived.', 'Could not archive this guest.');
  };
  const onRestore = () => run(() => restoreGuest(guest.id), 'Guest restored.', 'Could not restore this guest.');
  const onDeleteForever = async () => {
    if (!canDeleteGuestPermanently(guest)) return;
    if (!(await confirm({
      title: 'Delete this guest forever?', subject: guest.name, variant: 'danger', confirmLabel: 'Delete forever',
      consequences: ['The guest and their answers are removed for good.'],
    }))) return;
    setBusy(true);
    try { await deleteGuestPermanentOrThrow(guest.id); showSuccess('Guest deleted.'); navigate(GUESTS_PATH); }
    catch (err) { showError(permanentDeleteErrorMessage(err, 'guest')); }
    finally { setBusy(false); }
  };

  const onRsvp = async (eventId: string, value: string) => {
    try { await setGuestEventRsvp(eventId, guest.id, value as RSVPStatus); reloadInvitations(); }
    catch (err) { showError(readableErrorMessage(err, 'Could not change this answer.')); }
  };
  const onUninvite = async (eventId: string) => {
    try { await removeGuestFromEvent(eventId, guest.id); reloadInvitations(); }
    catch (err) { showError(readableErrorMessage(err, 'Could not remove this invitation.')); }
  };
  const onInvite = async () => {
    if (!eventToAdd) return;
    try { await setGuestEventRsvp(eventToAdd, guest.id, 'pending'); setEventToAdd(''); reloadInvitations(); }
    catch (err) { showError(readableErrorMessage(err, 'Could not invite this guest.')); }
  };

  return (
    <article className="gst-profile" aria-label={`Guest ${guest.name}`}>
      <header className="gst-profile__head">
        <div>
          <h2>{guest.name}{archived && <span className="gst-badge gst-badge--muted">Archived</span>}</h2>
          <p className="gst-profile__sub">
            <span>{guest.id}</span>
            {guest.role && <span> · {guestRoleLabel(guest.role)}</span>}
            {guest.is_honoree && <span> · Honoree</span>}
          </p>
        </div>
        <div className="gst-profile__actions">
          <Button variant="outline" size="sm" onClick={() => navigate(GUESTS_PATH)}>Back</Button>
          {isPlanner && !archived && <Button variant="primary" size="sm" onClick={() => navigate(guestEditPath(guest.id))}>Edit</Button>}
          {isPlanner && !archived && <Button variant="outline" size="sm" disabled={busy} onClick={onArchive}>Archive</Button>}
          {isPlanner && archived && <Button variant="primary" size="sm" disabled={busy} onClick={onRestore}>Restore</Button>}
          {isPlanner && archived && <Button variant="danger" size="sm" disabled={busy} onClick={onDeleteForever}>Delete forever</Button>}
        </div>
      </header>

      <section className="gst-profile__grid">
        <dl className="gst-dl">
          <dt>Email</dt><dd>{guest.email || '—'}</dd>
          <dt>Phone</dt><dd>{guest.phone || '—'}</dd>
          <dt>Side</dt><dd>{side ? sideLabel(side) : 'Not set'}</dd>
          <dt>RSVP</dt>
          <dd>
            {RSVP_LABEL[status] ?? status}
            {hasRsvpMismatch(guest) && <span className="gst-badge gst-badge--warn" title="The stored answer differs from the answers per event">Differs from stored answer</span>}
          </dd>
          {canSeeInviteCode && <><dt>Invite code</dt><dd>{inviteCode ? <MaskedValue key={guestRef} value={inviteCode} visibleChars={0} label="invite code" /> : '—'}</dd></>}
          <dt>Dietary needs</dt><dd>{guest.dietary_restrictions?.length ? guest.dietary_restrictions.join(', ') : '—'}</dd>
          <dt>Notes</dt><dd>{guest.notes || '—'}</dd>
        </dl>
        <div>
          <h3>Tags</h3>
          {guest.tags.length === 0 ? <p className="form-helper">No tags.</p> : (
            <ul className="gst-tags">{guest.tags.map(t => <li key={t.id} className="gst-tag" style={{ /* ds-dynamic: tag colour from the project's tag catalogue */ '--gst-tag-color': tagColours.get(t.id) ?? t.color } as React.CSSProperties}>{t.label}</li>)}</ul>
          )}
          <h3>Plus-ones</h3>
          {partyOf && <p>Plus-one of <a href={guestPath(partyOf.id)} onClick={e => { e.preventDefault(); navigate(guestPath(partyOf.id)!); }}>{partyOf.name}</a></p>}
          {guest.plus_ones.length === 0 && plusOnes.length === 0 && !partyOf && <p className="form-helper">No plus-ones.</p>}
          <ul className="gst-plusones">
            {guest.plus_ones.map(p => <li key={p.id}><span>{p.name || 'Unnamed plus-one'}</span></li>)}
            {plusOnes.map(g => (
              <li key={g.id}><a href={guestPath(g.id)} onClick={e => { e.preventDefault(); navigate(guestPath(g.id)!); }}>{g.name}</a>{g.archived && <span className="gst-badge gst-badge--muted">Archived</span>}</li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h3>Events</h3>
        {invitations.length === 0 ? <p className="form-helper">Not invited to any event yet.</p> : (
          <table className="dt gst-events" aria-label="Answers per event">
            <thead><tr><th>Event</th><th>Answer</th>{isPlanner && <th aria-label="Actions" />}</tr></thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.event_id}>
                  <td>{inv.event_title}</td>
                  <td>
                    {isPlanner && !archived
                      ? <SearchableSelect value={inv.rsvp_status} options={RSVP_CHOICES} onChange={v => onRsvp(inv.event_id, v)} />
                      : RSVP_LABEL[inv.rsvp_status] ?? inv.rsvp_status}
                  </td>
                  {isPlanner && (
                    <td>
                      {!archived && <button type="button" className="btn-icon" title="Remove this invitation" aria-label={`Remove invitation to ${inv.event_title}`} onClick={() => onUninvite(inv.event_id)}><Icon name="trash" size={14} /></button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {isPlanner && !archived && eventOptions.length > 0 && (
          <div className="gst-relations__add">
            <SearchableSelect value={eventToAdd} options={eventOptions} placeholder="Invite to an event…" onChange={setEventToAdd} />
            <Button size="sm" variant="outline" onClick={onInvite} disabled={!eventToAdd}>Invite</Button>
          </div>
        )}
      </section>

      <GuestRelationsPanel guest={guest} editable={isPlanner && !archived} />
    </article>
  );
};

export default GuestProfile;
